import React, { useState, useRef, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Play, MapPin, Shield, Heart, Globe, Droplets } from 'lucide-react';

const CHILD_IMAGE = "https://customer-assets.emergentagent.com/job_liquid-dignity/artifacts/rk0hr1zl_WhatsApp%20Image%202026-03-14%20at%2018.11.51.jpeg";

// Color configurations for amounts
const amountColors = {
  10: { bg: 'from-cyan-500 to-blue-600', text: 'text-cyan-400', border: 'border-cyan-500/50', icon: '💧' },
  50: { bg: 'from-emerald-500 to-teal-600', text: 'text-emerald-400', border: 'border-emerald-500/50', icon: '🏠' },
  100: { bg: 'from-rose-500 to-pink-600', text: 'text-rose-400', border: 'border-rose-500/50', icon: '🏫' },
  500: { bg: 'from-amber-500 to-orange-600', text: 'text-amber-400', border: 'border-amber-500/50', icon: '🌍' },
};

const content = {
  en: {
    eyebrow: 'DONATE NOW',
    headline: 'Your Legacy',
    headlineAccent: 'Begins Here',
    subheadline: 'Every contribution brings clean water to families in need.',
    secure: 'Secure payment powered by Stripe via Donorbox',
    referenceNote: 'These amounts are suggested references. You may donate any amount you wish to our noble cause.',
    everyDropCounts: 'Every dollar and every drop counts.',
    impact: {
      title: 'Your Impact',
      subtitle: 'What your donation provides',
      items: [
        { amount: 10, desc: 'Clean water for 1 child' },
        { amount: 50, desc: 'Water filter for a family' },
        { amount: 100, desc: 'School filtration system' },
        { amount: 500, desc: 'Community water well' },
      ],
    },
    liveCounter: { title: 'Lives Impacted', subtitle: 'And counting...' },
    goal: { title: 'Progress', percentage: 'Complete' },
    map: {
      title: 'Where We Work',
      subtitle: 'Communities across the globe',
      locations: ['Kenya', 'Uganda', 'Ghana', 'India', 'Bangladesh', 'Cambodia'],
    },
    video: { title: 'Stories of Hope', subtitle: 'Real communities', placeholder: 'Video coming soon', cta: 'Watch' },
    trust: { secure: '256-bit SSL', registered: 'UK Registered', transparent: 'Transparent' },
  },
  es: {
    eyebrow: 'DONA AHORA',
    headline: 'Tu Legado',
    headlineAccent: 'Comienza Aquí',
    subheadline: 'Cada contribución lleva agua limpia a familias necesitadas.',
    secure: 'Pago seguro con Stripe vía Donorbox',
    referenceNote: 'Estos montos son referencias sugeridas. Puede donar la cantidad que desee.',
    everyDropCounts: 'Cada dólar y cada gota cuenta.',
    impact: {
      title: 'Tu Impacto',
      subtitle: 'Lo que proporciona tu donación',
      items: [
        { amount: 10, desc: 'Agua para 1 niño' },
        { amount: 50, desc: 'Filtro para una familia' },
        { amount: 100, desc: 'Filtración escolar' },
        { amount: 500, desc: 'Pozo comunitario' },
      ],
    },
    liveCounter: { title: 'Vidas Impactadas', subtitle: 'Y contando...' },
    goal: { title: 'Progreso', percentage: 'Completado' },
    map: {
      title: 'Dónde Trabajamos',
      subtitle: 'Comunidades en todo el mundo',
      locations: ['Kenia', 'Uganda', 'Ghana', 'India', 'Bangladesh', 'Camboya'],
    },
    video: { title: 'Historias de Esperanza', subtitle: 'Comunidades reales', placeholder: 'Video próximamente', cta: 'Ver' },
    trust: { secure: 'SSL 256-bit', registered: 'UK Registrado', transparent: 'Transparente' },
  },
};

const counterColors = ['from-rose-400 to-pink-300', 'from-emerald-400 to-teal-300', 'from-cyan-400 to-blue-300', 'from-amber-400 to-orange-300'];

// Live Counter - More colorful
const LiveCounter = ({ language }) => {
  const [count, setCount] = useState(51847);
  const [colorIndex, setColorIndex] = useState(0);
  const t = content[language].liveCounter;
  
  useEffect(() => {
    const interval = setInterval(() => {
      setCount(prev => prev + Math.floor(Math.random() * 7) + 1);
      setColorIndex(prev => (prev + 1) % counterColors.length);
    }, 4500);
    return () => clearInterval(interval);
  }, []);

  return (
    <motion.div initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }}
      className="text-center mb-8 sm:mb-12 py-6 sm:py-8 rounded-2xl sm:rounded-3xl bg-gradient-to-br from-rose-500/5 via-transparent to-cyan-500/5 border border-white/10">
      <p className="text-xs sm:text-sm text-rose-400 uppercase tracking-widest mb-2 sm:mb-3 font-medium">{t.title}</p>
      <AnimatePresence mode="wait">
        <motion.p key={count} initial={{ scale: 1.05, opacity: 0 }} animate={{ scale: 1, opacity: 1 }} exit={{ scale: 0.95, opacity: 0 }}
          className={`text-4xl sm:text-5xl md:text-6xl font-bold bg-gradient-to-r ${counterColors[colorIndex]} bg-clip-text text-transparent`}>
          {count.toLocaleString()}
        </motion.p>
      </AnimatePresence>
      <p className="text-xs sm:text-sm text-cyan-400/60 mt-2 sm:mt-3">{t.subtitle}</p>
      <div className="flex justify-center gap-2 mt-3 sm:mt-4">
        {['bg-rose-400', 'bg-emerald-400', 'bg-cyan-400'].map((c, i) => (
          <motion.div key={i} className={`w-1.5 h-1.5 sm:w-2 sm:h-2 rounded-full ${c}`}
            animate={{ y: [0, -5, 0], opacity: [0.5, 1, 0.5] }} transition={{ duration: 2, repeat: Infinity, delay: i * 0.3 }} />
        ))}
      </div>
    </motion.div>
  );
};

// Progress Bar - More colorful, NO dollar amounts
const ProgressBar = ({ language }) => {
  const t = content[language].goal;
  const percentage = 48;

  return (
    <motion.div initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }}
      className="mb-8 sm:mb-12 p-4 sm:p-5 rounded-xl sm:rounded-2xl bg-gradient-to-r from-rose-500/10 via-emerald-500/5 to-cyan-500/10 border border-white/10">
      <div className="flex justify-between items-center mb-2 sm:mb-3">
        <h3 className="text-sm sm:text-base font-medium bg-gradient-to-r from-rose-400 to-cyan-400 bg-clip-text text-transparent">{t.title}</h3>
        <span className="text-lg sm:text-xl font-bold text-emerald-400">{percentage}% <span className="text-xs sm:text-sm font-normal text-emerald-400/70">{t.percentage}</span></span>
      </div>
      <div className="h-2 sm:h-3 bg-white/10 rounded-full overflow-hidden">
        <motion.div initial={{ width: 0 }} whileInView={{ width: `${percentage}%` }} viewport={{ once: true }}
          transition={{ duration: 1.5, ease: "easeOut" }} className="h-full rounded-full bg-gradient-to-r from-rose-500 via-emerald-500 to-cyan-500" />
      </div>
    </motion.div>
  );
};

// World Map - Compact for mobile
const WorldMap = ({ language }) => {
  const t = content[language].map;
  return (
    <motion.div initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }}
      className="p-4 sm:p-5 rounded-xl sm:rounded-2xl bg-gradient-to-br from-emerald-500/10 via-transparent to-cyan-500/10 border border-white/10">
      <h3 className="text-sm sm:text-base font-medium bg-gradient-to-r from-emerald-400 to-cyan-400 bg-clip-text text-transparent mb-1 sm:mb-2">{t.title}</h3>
      <p className="text-[10px] sm:text-xs text-white/40 mb-2 sm:mb-3">{t.subtitle}</p>
      <div className="relative h-20 sm:h-28 rounded-xl overflow-hidden bg-gradient-to-b from-emerald-900/30 to-cyan-900/30">
        <div className="absolute inset-0 flex items-center justify-center">
          <div className="w-14 sm:w-20 h-14 sm:h-20 rounded-full border-2 border-emerald-500/30 relative">
            <div className="absolute inset-1 sm:inset-2 rounded-full border border-cyan-500/30" />
            <div className="absolute inset-2 sm:inset-4 rounded-full border border-rose-500/30" />
            {[{ top: '20%', left: '60%' }, { top: '35%', left: '45%' }, { top: '40%', left: '50%' }, { top: '30%', left: '70%' }, { top: '45%', left: '75%' }, { top: '50%', left: '42%' }].map((pos, i) => (
              <motion.div key={i} className="absolute w-1 sm:w-1.5 h-1 sm:h-1.5 rounded-full bg-rose-400" style={pos}
                animate={{ scale: [1, 1.5, 1], opacity: [0.7, 1, 0.7] }} transition={{ duration: 2, repeat: Infinity, delay: i * 0.3 }} />
            ))}
          </div>
        </div>
        <div className="absolute bottom-1 sm:bottom-2 left-0 right-0 flex flex-wrap justify-center gap-0.5 sm:gap-1 px-1 sm:px-2">
          {t.locations.slice(0, 4).map((loc, i) => (
            <span key={i} className="px-1 sm:px-1.5 py-0.5 text-[7px] sm:text-[9px] rounded-full bg-emerald-500/20 text-emerald-300 flex items-center gap-0.5">
              <MapPin className="w-1.5 sm:w-2 h-1.5 sm:h-2" />{loc}
            </span>
          ))}
        </div>
      </div>
    </motion.div>
  );
};

// Video Placeholder - Compact
const VideoPlaceholder = ({ language }) => {
  const t = content[language].video;
  return (
    <motion.div initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }}>
      <h3 className="text-sm sm:text-base font-medium bg-gradient-to-r from-rose-400 to-amber-400 bg-clip-text text-transparent mb-1 sm:mb-2">{t.title}</h3>
      <p className="text-[10px] sm:text-xs text-white/40 mb-1 sm:mb-2">{t.subtitle}</p>
      <div className="relative aspect-video rounded-xl overflow-hidden bg-gradient-to-br from-rose-500/20 via-emerald-500/10 to-cyan-500/20 border border-white/10 group cursor-pointer">
        <img src={CHILD_IMAGE} alt="Video" className="w-full h-full object-cover opacity-50 group-hover:opacity-70 transition-opacity" />
        <div className="absolute inset-0 bg-gradient-to-t from-[#0A1F3A]/80 via-transparent to-[#0A1F3A]/40" />
        <div className="absolute inset-0 flex items-center justify-center">
          <motion.div whileHover={{ scale: 1.1 }} className="w-10 sm:w-12 h-10 sm:h-12 rounded-full bg-gradient-to-br from-rose-500 to-cyan-500 flex items-center justify-center shadow-2xl shadow-rose-500/30">
            <Play className="w-4 sm:w-5 h-4 sm:h-5 text-white ml-0.5" fill="white" />
          </motion.div>
        </div>
        <div className="absolute bottom-1 sm:bottom-2 left-1 sm:left-2 right-1 sm:right-2 flex items-center justify-between">
          <span className="px-1.5 sm:px-2 py-0.5 rounded-full bg-rose-500/20 backdrop-blur-sm text-[8px] sm:text-[10px] text-rose-300">{t.placeholder}</span>
          <span className="text-[8px] sm:text-[10px] text-cyan-400">{t.cta} →</span>
        </div>
      </div>
    </motion.div>
  );
};

// Donorbox Widget - FUNCTIONAL
const DonorboxWidget = ({ language }) => {
  const t = content[language];
  const [isLoaded, setIsLoaded] = useState(false);

  return (
    <motion.div initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} className="relative" id="donorbox-form">
      <div className="text-center mb-4 sm:mb-6">
        <span className="inline-block px-3 sm:px-4 py-1 sm:py-1.5 rounded-full bg-gradient-to-r from-rose-500/20 to-cyan-500/20 border border-rose-500/30 text-rose-300 text-[10px] sm:text-xs font-medium tracking-widest mb-2 sm:mb-3">
          {t.eyebrow}
        </span>
        <h2 className="text-xl sm:text-2xl md:text-3xl font-light mb-1 sm:mb-2">
          <span className="text-white/90">{t.headline} </span>
          <span className="bg-gradient-to-r from-rose-400 via-pink-400 to-cyan-400 bg-clip-text text-transparent">{t.headlineAccent}</span>
        </h2>
        <p className="text-white/40 font-light text-xs sm:text-sm max-w-md mx-auto">{t.subheadline}</p>
      </div>

      <div className="relative rounded-xl sm:rounded-2xl overflow-hidden">
        <div className="absolute -inset-1 rounded-xl sm:rounded-2xl bg-gradient-to-r from-rose-500/20 via-cyan-500/15 to-emerald-500/20 blur-lg opacity-60" />
        <div className="relative rounded-xl sm:rounded-2xl overflow-hidden bg-[#0d1829] border border-white/10 shadow-2xl">
          {/* Dark overlay */}
          <div className="absolute inset-0 pointer-events-none z-10">
            <div className="absolute inset-x-0 top-0 h-12 sm:h-16 bg-gradient-to-b from-[#0A1F3A]/80 to-transparent" />
            <div className="absolute inset-y-0 left-0 w-3 sm:w-4 bg-gradient-to-r from-[#0A1F3A]/60 to-transparent" />
            <div className="absolute inset-y-0 right-0 w-3 sm:w-4 bg-gradient-to-l from-[#0A1F3A]/60 to-transparent" />
            <div className="absolute inset-x-0 bottom-0 h-10 sm:h-12 bg-gradient-to-t from-[#0A1F3A]/70 to-transparent" />
          </div>
          
          {!isLoaded && (
            <div className="absolute inset-0 flex items-center justify-center bg-[#0d1829] z-20">
              <div className="text-center">
                <motion.div animate={{ rotate: 360 }} transition={{ duration: 2, repeat: Infinity, ease: "linear" }}
                  className="w-8 sm:w-10 h-8 sm:h-10 rounded-full border-2 border-rose-500/30 border-t-rose-500 mx-auto mb-2 sm:mb-3" />
                <p className="text-white/60 text-[10px] sm:text-xs">Loading...</p>
              </div>
            </div>
          )}
          
          <div className="relative z-0 p-2 sm:p-3 md:p-4">
            <iframe 
              src="https://donorbox.org/embed/every-drop-counts-donate-water-donate-life?amount=30&show_content=true&hide_donation_meter=true"
              name="donorbox" allowpaymentrequest="allowpaymentrequest" seamless="seamless" frameBorder="0" scrolling="no"
              height="900px" width="100%"
              style={{ maxWidth: '480px', minWidth: '250px', maxHeight: 'none', margin: '0 auto', display: 'block', borderRadius: '12px', background: 'transparent' }}
              allow="payment" title="Donorbox" onLoad={() => setIsLoaded(true)} data-testid="donorbox-iframe"
            />
          </div>
        </div>
      </div>

      <div className="mt-4 sm:mt-6 flex flex-wrap justify-center gap-2 sm:gap-4">
        <div className="flex items-center gap-1 text-[10px] sm:text-xs text-emerald-400/70"><Shield className="w-3 sm:w-3.5 h-3 sm:h-3.5" /><span>{t.trust.secure}</span></div>
        <div className="flex items-center gap-1 text-[10px] sm:text-xs text-cyan-400/70"><Globe className="w-3 sm:w-3.5 h-3 sm:h-3.5" /><span>{t.trust.registered}</span></div>
        <div className="flex items-center gap-1 text-[10px] sm:text-xs text-rose-400/70"><Heart className="w-3 sm:w-3.5 h-3 sm:h-3.5" /><span>{t.trust.transparent}</span></div>
      </div>
    </motion.div>
  );
};

// Impact Cards - Colorful, responsive
const ImpactCards = ({ language }) => {
  const t = content[language];
  
  const scrollToForm = () => {
    document.getElementById('donorbox-form')?.scrollIntoView({ behavior: 'smooth', block: 'start' });
  };

  return (
    <motion.div initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} className="space-y-3 sm:space-y-4">
      <div className="flex items-center justify-between mb-2 sm:mb-3">
        <h3 className="text-sm sm:text-base font-medium bg-gradient-to-r from-amber-400 to-rose-400 bg-clip-text text-transparent">{t.impact.title}</h3>
        <span className="text-[9px] sm:text-[10px] text-white/30">{t.impact.subtitle}</span>
      </div>
      
      <div className="grid grid-cols-2 gap-2 sm:space-y-0 sm:grid-cols-1 sm:gap-2">
        {t.impact.items.map((item, i) => {
          const c = amountColors[item.amount];
          return (
            <motion.div key={i} onClick={scrollToForm}
              initial={{ opacity: 0, x: 20 }} whileInView={{ opacity: 1, x: 0 }} viewport={{ once: true }} transition={{ delay: i * 0.08 }}
              whileHover={{ scale: 1.02 }} whileTap={{ scale: 0.98 }}
              className={`flex flex-col sm:flex-row items-center gap-2 sm:gap-3 p-2 sm:p-3 rounded-xl cursor-pointer bg-gradient-to-r ${c.bg}/10 border ${c.border} hover:${c.border.replace('/50', '')} transition-all`}>
              <div className={`w-8 sm:w-11 h-8 sm:h-11 rounded-lg flex items-center justify-center text-base sm:text-xl bg-gradient-to-br ${c.bg} shadow-lg flex-shrink-0`}>{c.icon}</div>
              <div className="flex-1 min-w-0 text-center sm:text-left">
                <p className="text-white/70 text-[10px] sm:text-xs font-medium line-clamp-2 sm:truncate">{item.desc}</p>
              </div>
              <p className={`text-lg sm:text-xl font-bold ${c.text}`}>${item.amount}</p>
            </motion.div>
          );
        })}
      </div>

      <div className="p-2 sm:p-3 rounded-xl bg-gradient-to-r from-rose-500/5 via-transparent to-cyan-500/5 border border-white/5">
        <p className="text-[9px] sm:text-[11px] text-white/30 italic text-center leading-relaxed">"{t.referenceNote}"</p>
        <p className="text-[9px] sm:text-[10px] text-center mt-1 flex items-center justify-center gap-1">
          <Droplets className="w-2.5 sm:w-3 h-2.5 sm:h-3 text-rose-400" />
          <span className="font-medium bg-gradient-to-r from-rose-400 to-cyan-400 bg-clip-text text-transparent">{t.everyDropCounts}</span>
        </p>
      </div>
    </motion.div>
  );
};

export const DonationForm = ({ language }) => {
  const ref = useRef(null);

  return (
    <section id="donate" ref={ref} className="relative py-16 sm:py-24 overflow-hidden" data-testid="donation-section">
      <div className="absolute inset-0 bg-gradient-to-b from-[#0A1F3A] via-[#0A1820] to-[#0A1F3A]" />
      <div className="absolute top-1/4 left-0 w-60 sm:w-80 h-60 sm:h-80 bg-rose-600/5 rounded-full blur-3xl" />
      <div className="absolute bottom-1/4 right-0 w-60 sm:w-80 h-60 sm:h-80 bg-emerald-600/5 rounded-full blur-3xl" />
      <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-60 sm:w-80 h-60 sm:h-80 bg-cyan-600/5 rounded-full blur-3xl" />

      <div className="relative z-10 max-w-6xl mx-auto px-3 sm:px-4 md:px-6">
        <LiveCounter language={language} />
        <ProgressBar language={language} />

        <div className="grid lg:grid-cols-2 gap-6 sm:gap-8 lg:gap-10 items-start">
          <DonorboxWidget language={language} />
          <motion.div initial={{ opacity: 0, x: 30 }} whileInView={{ opacity: 1, x: 0 }} viewport={{ once: true }} className="space-y-4 sm:space-y-5">
            <ImpactCards language={language} />
            <WorldMap language={language} />
            <VideoPlaceholder language={language} />
          </motion.div>
        </div>
      </div>
    </section>
  );
};

export default DonationForm;
